import { supabase } from '@/integrations/supabase/client';

interface RpcError {
  readonly code?: string;
  readonly message?: string;
}

interface EnterpriseLifecycleClient {
  rpc(
    functionName: string,
    parameters: Readonly<Record<string, unknown>>,
  ): Promise<{ data: unknown; error: RpcError | null }>;
}

const enterpriseDb = supabase as unknown as EnterpriseLifecycleClient;

export const AGENT_DEPENDENCY_LABELS = {
  evaluationCount: 'completed evaluations',
  coachingSessionCount: 'coaching sessions',
  userAssignmentCount: 'user assignments',
  enterpriseAssignmentCount: 'organisation access assignments',
  pathwayModuleCount: 'learning pathway modules',
} as const;

export type AgentDependencyKey = keyof typeof AGENT_DEPENDENCY_LABELS;

export interface AgentDeletionEligibility {
  readonly agentId: string;
  readonly agentName: string;
  readonly status: 'active' | 'inactive';
  readonly isSystem: boolean;
  readonly canPermanentlyDelete: boolean;
  readonly evaluationCount: number;
  readonly coachingSessionCount: number;
  readonly userAssignmentCount: number;
  readonly enterpriseAssignmentCount: number;
  readonly pathwayModuleCount: number;
}

type AgentDeletionEligibilityRow = {
  agent_id: string;
  agent_name: string;
  agent_status: 'active' | 'inactive';
  is_system: boolean;
  can_permanently_delete: boolean;
  evaluation_count: number | string;
  coaching_session_count: number | string;
  user_assignment_count: number | string;
  enterprise_assignment_count: number | string;
  pathway_module_count: number | string;
};

function toCount(value: number | string): number {
  const count = Number(value);
  if (!Number.isSafeInteger(count) || count < 0) {
    throw new Error('The server returned an invalid agent dependency count.');
  }
  return count;
}

function normaliseEligibility(row: AgentDeletionEligibilityRow): AgentDeletionEligibility {
  if (!row?.agent_id || !row.agent_name || !['active', 'inactive'].includes(row.agent_status)) {
    throw new Error('The server returned an invalid agent lifecycle response.');
  }

  return {
    agentId: row.agent_id,
    agentName: row.agent_name,
    status: row.agent_status,
    isSystem: Boolean(row.is_system),
    canPermanentlyDelete: Boolean(row.can_permanently_delete),
    evaluationCount: toCount(row.evaluation_count),
    coachingSessionCount: toCount(row.coaching_session_count),
    userAssignmentCount: toCount(row.user_assignment_count),
    enterpriseAssignmentCount: toCount(row.enterprise_assignment_count),
    pathwayModuleCount: toCount(row.pathway_module_count),
  };
}

export function getBlockingAgentDependencies(
  eligibility: AgentDeletionEligibility,
): ReadonlyArray<{ key: AgentDependencyKey; label: string; count: number }> {
  return (Object.entries(AGENT_DEPENDENCY_LABELS) as Array<[AgentDependencyKey, string]>)
    .map(([key, label]) => ({ key, label, count: eligibility[key] as number }))
    .filter(({ count }) => count > 0);
}

export async function setAgentArchived(agentId: string, archived: boolean): Promise<'active' | 'inactive'> {
  const { data, error } = await enterpriseDb.rpc('set_enterprise_agent_archived', {
    p_agent_id: agentId,
    p_archived: archived,
  });

  if (error) throw new Error(error.message || 'Unable to update the agent lifecycle.');
  const row = (Array.isArray(data) ? data[0] : data) as { status?: string } | null;
  if (row?.status !== 'active' && row?.status !== 'inactive') {
    throw new Error('The server did not confirm the agent lifecycle change.');
  }
  return row.status;
}

export async function fetchAgentDeletionEligibility(agentId: string): Promise<AgentDeletionEligibility> {
  const { data, error } = await enterpriseDb.rpc('get_enterprise_agent_deletion_eligibility', {
    p_agent_id: agentId,
  });

  if (error) throw new Error(error.message || 'Unable to check whether this agent can be deleted.');
  const row = (Array.isArray(data) ? data[0] : data) as AgentDeletionEligibilityRow | null;
  if (!row) throw new Error('The server did not return agent deletion eligibility.');
  return normaliseEligibility(row);
}

export async function permanentlyDeleteAgent(agentId: string, confirmationName: string): Promise<void> {
  const { data, error } = await enterpriseDb.rpc('delete_enterprise_agent_permanently', {
    p_agent_id: agentId,
    p_confirmation_name: confirmationName,
  });

  if (error) {
    if (error.code === '23503') {
      throw new Error('This agent has operational history or assignments and cannot be permanently deleted.');
    }
    throw new Error(error.message || 'Unable to permanently delete this agent.');
  }

  const row = (Array.isArray(data) ? data[0] : data) as { deleted_agent_id?: string } | null;
  if (row?.deleted_agent_id !== agentId) {
    throw new Error('The server did not confirm permanent deletion.');
  }
}
